import { useEffect, useState } from "react";
import {
  Checkbox,
  FormModal,
  Input,
  ResponsiveGrid,
  Select,
  Text,
} from "@marekl/adminer/components";
import {
  BLOCK_DEFAULT_MINUTES,
  BLOCK_KIND_LABELS,
  BLOCK_KIND_OPTIONS,
  BLOCK_PARALLEL_BY_DEFAULT,
  formatDayTime,
  parseDayTime,
} from "../../../lib/startListState";
import type {
  BlockPayload,
  StartListBlockKind,
  StartListItem,
} from "../../../lib/queries/startlists";

interface BlockModalProps {
  open: boolean;
  /** Set when editing; absent when inserting. */
  item?: StartListItem;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (payload: BlockPayload) => void;
}

/**
 * The one dialog for a manual block.
 *
 * The parallel switch is the important control, and it changes what the
 * rest of the form asks for: a sequential block has a LENGTH (it pushes
 * everything after it), a parallel one has a WINDOW (it runs alongside
 * and pushes nothing). Asking for both would invite an organizer to fill
 * in a duration that is then silently ignored.
 *
 * Prezence and prostorové zkoušky default to parallel, because that is
 * what they are; přestávka and vyhlášení default to sequential.
 */
export function BlockModal({
  open,
  item,
  pending,
  error,
  onClose,
  onSubmit,
}: BlockModalProps) {
  const [kind, setKind] = useState<StartListBlockKind>("break");
  const [label, setLabel] = useState("");
  const [minutes, setMinutes] = useState(15);
  const [parallel, setParallel] = useState(false);
  const [from, setFrom] = useState("06:00");
  const [to, setTo] = useState("14:00");

  useEffect(() => {
    if (!open) return;
    if (item) {
      const k = item.block_kind ?? "other";
      setKind(k);
      setLabel(item.label ?? "");
      setMinutes(Math.round((item.duration_seconds ?? 0) / 60));
      setParallel(item.parallel);
    } else {
      setKind("break");
      setLabel("");
      setMinutes(BLOCK_DEFAULT_MINUTES.break);
      setParallel(false);
    }
  }, [open, item]);

  const pickKind = (next: StartListBlockKind) => {
    setKind(next);
    // Only nudge while the block is new — silently rewriting an edited
    // block's values would undo a deliberate choice.
    if (item) return;
    setMinutes(BLOCK_DEFAULT_MINUTES[next]);
    setParallel(BLOCK_PARALLEL_BY_DEFAULT.has(next));
  };

  const submit = () => {
    if (parallel) {
      onSubmit({
        block_kind: kind,
        label: label.trim(),
        parallel: true,
        start_seconds: parseDayTime(from),
        end_seconds: parseDayTime(to),
      });
      return;
    }
    onSubmit({
      block_kind: kind,
      label: label.trim(),
      parallel: false,
      duration_seconds: Math.max(0, Math.round(minutes)) * 60,
    });
  };

  return (
    <FormModal
      open={open}
      title={item ? "Upravit blok" : "Vložit blok"}
      submitLabel={item ? "Uložit" : "Vložit"}
      busy={pending}
      error={error}
      onSubmit={submit}
      onClose={onClose}
    >
      <Select
        label="Druh bloku"
        value={kind}
        onChange={(e) => pickKind(e.target.value as StartListBlockKind)}
      >
        {BLOCK_KIND_OPTIONS.map((k) => (
          <option key={k} value={k}>
            {BLOCK_KIND_LABELS[k]}
          </option>
        ))}
      </Select>
      <Input
        label="Popis"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={BLOCK_KIND_LABELS[kind]}
        hint="Nepovinné; bez popisu se vypíše druh bloku."
      />

      <Checkbox
        label="Probíhá souběžně s listinou"
        checked={parallel}
        onChange={(e) => setParallel(e.target.checked)}
        description={
          parallel
            ? "Blok se vypíše vedle listiny a časy vystoupení neposune."
            : "Blok se zařadí mezi vystoupení a všechno za ním posune."
        }
      />

      {parallel ? (
        <ResponsiveGrid columns={2}>
          <Input label="Od" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Input label="Do" value={to} onChange={(e) => setTo(e.target.value)} />
        </ResponsiveGrid>
      ) : (
        <Input
          label="Délka (minuty)"
          type="number"
          min={0}
          value={String(minutes)}
          onChange={(e) => setMinutes(Number(e.target.value))}
        />
      )}

      {parallel && (
        <Text variant="muted">
          Například prezence {formatDayTime(parseDayTime(from))}–
          {formatDayTime(parseDayTime(to))}: běží po celou dobu, ale nikoho nezdrží.
        </Text>
      )}
    </FormModal>
  );
}
